import React from 'react';
import styled, { css } from 'styled-components/macro';
import { CloseIcon } from 'assets/icons';
import { PopoverName } from 'shared/enums/PopoverName';
import Popover from 'shared/providers/PopoverWindowProvider/components/Popover';
import { PopoverSizes } from 'shared/providers/PopoverWindowProvider/enums/PopoverSizes';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import { WarningIcon } from 'assets/icons';
import { ReactComponent as ChromeIcon } from 'assets/images/chrome.svg';
import { ReactComponent as FirefoxIcon } from 'assets/images/firefox.svg';
import { ReactComponent as SafariIcon } from 'assets/images/safari.svg';
import { ReactComponent as EdgeIcon } from 'assets/images/edge.svg';
import { getSupportedBrowsers } from './checkBrowserSupport';
import { Browsers } from 'shared/enums/Browsers';

interface IUnsupportedBrowserPopoverProps {
  startTop?: number;
  startLeft?: number;
  closePopover: () => void;
}

const UnsupportedBrowserPopover = ({ closePopover }: IUnsupportedBrowserPopoverProps): JSX.Element => {
  const { isMobile } = useResolutionProvider();

  const supportedBrowsers = getSupportedBrowsers();

  const getSupportedBrowserIcon = (browser: string): JSX.Element | undefined => {
    switch (browser) {
      case Browsers.Chrome:
        return <StyledChromeIcon />;
      case Browsers.Firefox:
        return <StyledFirefoxIcon />;
      case Browsers.Safari:
      case Browsers.MobileSafari:
        return <StyledSafariIcon />;
      case Browsers.Edge:
        return <StyledEdgeIcon />;
      default:
        break;
    }
  };

  return (
    <Popover
      name={PopoverName.UnsupportedBrowser}
      size={PopoverSizes.sm}
      id={PopoverName.UnsupportedBrowser}
      startLeft={window.innerWidth / 2 - 200}
      isMobile={isMobile}
      width={400}
    >
      <MessageContainer>
        <Container>
          <Header>
            <div onClick={closePopover}>
              <StyledCloseIcon />
            </div>
          </Header>
          <Title>
            <StyledWarningIcon />
            Unsupported Browser
          </Title>
          <Info>Unfortunately, the browser you are using is incompatible with Otto phone calls.</Info>
          <MessageBody>
            Supported Browsers include:
            <br />
            <SupportedBrowsersContainer>
              {supportedBrowsers.map((browser, i) => {
                return (
                  <SupportedBrowser key={i}>
                    {getSupportedBrowserIcon(browser)}
                    {browser}
                  </SupportedBrowser>
                );
              })}
            </SupportedBrowsersContainer>
            Please try again with a supported browser.
          </MessageBody>
        </Container>
      </MessageContainer>
    </Popover>
  );
};

const MessageContainer = styled.div`
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 100%;
`;
const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;
const StyledWarningIcon = styled(WarningIcon)`
  width: 20px;
  height: auto;
  margin-right: 6px;
`;
const Title = styled.div`
  display: flex;
  font-size: 24px;
  font-weight: 700;
  padding: 3px 0;
`;
const Info = styled.div`
  padding: 0 0 15px 0;
  font-size: 20px;
`;
const MessageBody = styled.div`
  display: flex;
  flex-wrap: wrap;
  cursor: default;
  font-size: 18px;
  flex-direction: column;
  width: 100%;
`;
const SupportedBrowsersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 10px 0;
`;
const SupportedBrowser = styled.div`
  font-size: 14px;
  font-weight: 600;
  width: 100%;
  padding: 5px;
  display: flex;
  align-items: center;
`;
const StyledCloseIcon = styled(CloseIcon)`
  width: 14px;
  height: 100%;
  cursor: pointer;

  path {
    fill: var(--chakra-colors-text-default);
  }
`;
const BrowserIcon = css`
  width: auto;
  height: auto;
  max-height: 29px;
  margin-right: 6px;
`;
const StyledChromeIcon = styled(ChromeIcon)`
  ${BrowserIcon}
`;
const StyledFirefoxIcon = styled(FirefoxIcon)`
  ${BrowserIcon}
`;
const StyledSafariIcon = styled(SafariIcon)`
  ${BrowserIcon}
`;
const StyledEdgeIcon = styled(EdgeIcon)`
  ${BrowserIcon}
`;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 15px 15px 10px;
`;

export default UnsupportedBrowserPopover;
